import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback, useState } from "react";

import {
  type CreateDefaultSymptomSchema,
  SupportedSymptom,
  apiPostHfSymptoms,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { useSupportedSymptoms } from "../../../catalogs/supported-symptoms";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { Checkbox } from "../../../components/ui-kit/fields/checkbox";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import {
  RowAccordion,
  useRowAccordion,
} from "../../../components/ui-kit/tables/row-accordion";
import { getNamesValues } from "../../../utils/data-utils";
import { getServerNowISO } from "../../../utils/server-time";
import { createRowId, maybeCapitalizeFirstLetter } from "../../../utils/utils";

export const AddSymptomsDialog = observer(function AddSymptomsDialog({
  app,
  onClose,
  onSaved,
}: {
  app: AppModel;
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      className="top-[5dvh] max-h-[90dvh] w-[750px] translate-y-0"
    >
      <AddSymptomsBody app={app} onClose={onClose} onSaved={onSaved} />
    </Dialog>
  );
});

const AddSymptomsBody = observer(function AddSymptomsBody({
  app,
  onClose,
  onSaved,
}: {
  app: AppModel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const patient = app.requirePatient();
  const api = app.seerlinqApi;
  const supportedSymptoms = useSupportedSymptoms();

  const hfSymptoms = supportedSymptoms.filter((s) =>
    HF_VALID_SYMPTOM_NAMES.has(s.data_name),
  );

  const createRow = useCallback(
    () => createSymptomRow(hfSymptoms[0]?.data_name ?? ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supportedSymptoms],
  );

  // hfSymptoms is derived synchronously, so seed one row per symptom up front
  // (computed once) instead of populating via an effect after the first render.
  const [initialRows] = useState(() =>
    hfSymptoms.map((s) => createSymptomRow(s.data_name)),
  );
  const accordion = useRowAccordion(createRow, initialRows);

  const hasValidRows =
    accordion.rows.length > 0 &&
    accordion.rows.every(
      (r) =>
        !!r.symptom_name && !!r.symptom_datetime && r.symptom_value != null,
    );

  async function save() {
    const items = accordion.rows.map((row) =>
      toSymptomPayload(row, patient.patientId),
    );

    try {
      const response = await apiPostHfSymptoms({
        body: {
          number_of_datapoints: items.length,
          symptoms: items,
        },
      });

      if (response.data != null) {
        api.clearResponseCache();
        onSaved();
        onClose();
      }
    } catch (error) {
      window.handleError(error);
    }
  }

  return (
    <>
      <h3 className="mb-6 text-base font-bold">
        <Trans>Add symptoms</Trans>
      </h3>

      <Form className="flex min-h-0 flex-1 flex-col" onSubmit={() => save()}>
        <div className="min-h-0 overflow-y-auto overflow-x-hidden py-1 px-1">
          <RowAccordion
            {...accordion}
            summaryLabel={(row) => (
              <>
                {maybeCapitalizeFirstLetter(row.symptom_name)}
                {row.symptom_value != null && (
                  <span className="text-muted-foreground">
                    — {row.symptom_value}
                  </span>
                )}
              </>
            )}
            renderRow={(row) => (
              <RowForm row={row} supportedSymptoms={hfSymptoms} />
            )}
          />
        </div>

        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit" disabled={!hasValidRows}>
            <Trans>Add</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            onClick={onClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </>
  );
});

const RowForm = observer(function RowForm({
  row,
  supportedSymptoms,
}: {
  row: SymptomRow;
  supportedSymptoms: SupportedSymptom[];
}) {
  const { t } = useLingui();
  return (
    <FormTable dataAdd className="max-w-full">
      <FormRow
        label={t`Symptom`}
        data={
          <Select
            model={row}
            attr="symptom_name"
            values={getNamesValues(supportedSymptoms)}
          />
        }
        mandatory
      />
      <FormRow
        label={t`Symptom datetime`}
        data={
          <DateInput
            model={row}
            attr="symptom_datetime"
            type="datetime-local"
          />
        }
        mandatory
      />
      <FormRow
        label={t`Value`}
        data={<NumberInput model={row} attr="symptom_value" />}
        mandatory
      />
      <FormRow
        label={t`Change in last 6 months?`}
        data={
          <Checkbox
            model={row}
            attr="symptom_change_in_last_six_m"
            label={t`Yes`}
          />
        }
      />
      {row.symptom_change_in_last_six_m && (
        <>
          <FormRow
            label={t`Change date`}
            data={
              <DateInput model={row} attr="symptom_change_date" type="date" />
            }
          />
          <FormRow
            label={t`Value before`}
            data={<NumberInput model={row} attr="symptom_value_before" />}
          />
        </>
      )}
      <FormRow
        label={t`Comment`}
        data={
          <TextInput
            model={row}
            attr="comment"
            multiline="autosize"
            width={330}
          />
        }
      />
    </FormTable>
  );
});

// The HF symptoms endpoint only accepts these symptom types (see /api/v1/hf/symptoms docs).
const HF_VALID_SYMPTOM_NAMES = new Set([
  "shortness of breath",
  "fatigue score",
]);

type SymptomRow = {
  __rowId: string;
  symptom_datetime: string;
  symptom_name: string;
  symptom_value: number | null;
  symptom_change_in_last_six_m: boolean;
  symptom_change_date: string | null;
  symptom_value_before: number | null;
  comment: string;
};

function createSymptomRow(symptomName: string): SymptomRow {
  return observable({
    __rowId: createRowId(),
    symptom_datetime: getServerNowISO(),
    symptom_name: symptomName,
    symptom_value: null,
    symptom_change_in_last_six_m: false,
    symptom_change_date: null,
    symptom_value_before: null,
    comment: "",
  });
}

function toSymptomPayload(
  row: SymptomRow,
  patientId: number,
): CreateDefaultSymptomSchema {
  if (row.symptom_value == null) {
    throw new Error("symptom_value is required");
  }
  return {
    symptom_name: row.symptom_name,
    symptom_datetime: row.symptom_datetime,
    symptom_value: row.symptom_value,
    symptom_change_in_last_six_m: row.symptom_change_in_last_six_m,
    symptom_change_date: row.symptom_change_date,
    symptom_value_before: row.symptom_value_before,
    // Empty comment is sent as null — the schema marks it optional/nullable.
    comment: row.comment || null,
    patient_id: patientId,
  };
}
